// Spur kontingent, Gerüst der Oberfläche (SPEC-KONTINGENT.md 4.1, 4.2; welle-2/kontingent/AUFTEILUNG.md 2.2, S3-0): die Sätze,
// die mehrere Seiten zeigen (Nutzung, Mein Zugang, Kontingent, Mitglieder, Tarif und Kosten, das Fenster „Mehr für …“), an
// einer Stelle und wörtlich aus der Spec. Reine Funktionen über den Vertrag in administration/kontingent.ts; die Seiten setzen
// nur noch zusammen. Ein Satz mit der Support-Adresse kommt als Satz { vor, link, nach }: Die Adresse ist ein Link wie in
// „Über“ (config/legal.ts, VITE_SUPPORT_EMAIL); ohne eingerichtete Adresse gilt die Ersatzform der Spec. Kein Satz nennt
// einen Betrag für die KI (Regel 1.1).
// Geprüft in frontend/tests/kontingent-texte.test.mjs.
import { legalLinks } from '../../config/legal';
import type { Anteil, BalkenZustand, KontingentOrganisation, Stufe, UeberTopf, ZusatzTopf } from '../kontingent';
import { groesseAnzeige, prozentAnzeige } from './prozent';
import { anzahlSatz, zustandsSatz } from '../../pages/einstellungen/nutzungsbalken';

export type Link = { text: string; href: string };
export type Satz = { vor: string; link: Link | null; nach: string };
export const satzText = (satz: Satz) => `${satz.vor}${satz.link?.text ?? ''}${satz.nach}`;
const ohneLink = (text: string): Satz => ({ vor: text, link: null, nach: '' });

/** Die Support-Adresse aus VITE_SUPPORT_EMAIL als Link, wie „Über“ ihn zeigt; null ohne gültige Adresse. */
export function supportLink(env: Parameters<typeof legalLinks>[0] = import.meta.env): Link | null {
  const support = legalLinks(env).find(link => link.key === 'support');
  return support ? { text: support.href.replace(/^mailto:/, ''), href: support.href } : null;
}

const MEHR_FIRMA = 'Kontingent für die ganze Firma gibt in diesem Monat nur der Anbieter von CLAiRIS frei';

/**
 * KT-35, kurze Form (Runde 3, N3-01): „Mehr Kontingent für die ganze Firma gibt in diesem Monat nur der Anbieter von CLAiRIS
 * frei: {Support-Adresse}.“; ohne Adresse endet sie nach „frei“. Steht bei KT-24 (Lage „Firma“, für die Verwaltung), KT-41,
 * KT-50 (dort nach einem Semikolon, klein), KT-55 ab FAST und im Fenster KT-43.
 */
export function anbieterKurz(link: Link | null, lage: { klein?: boolean } = {}): Satz {
  const anfang = `${lage.klein ? 'mehr' : 'Mehr'} ${MEHR_FIRMA}`;
  return link ? { vor: `${anfang}: `, link, nach: '.' } : ohneLink(`${anfang}.`);
}

/**
 * KT-35 auf der Seite „Kontingent“ (Runde 2, N2-01; Runde 3, N3-01): mit angebotenem Zukauf der Weg über „Zusatzkontingent
 * kaufen“, sonst der Weg zum Anbieter mit der Adresse; ohne Adresse „…; wenden Sie sich dazu an den Anbieter von CLAiRIS.“.
 * selbstMehrGeben: das Konto darf das Kontingent verteilen, und mindestens eine Person hat eine persönliche Grenze – dann
 * dahinter der eigene Weg „Einer einzelnen Person mit persönlicher Grenze geben Sie selbst mehr …“.
 * ueberSchonGenannt (Schlusslauf 6, BA-6): darüber steht „Verbrauch über dem Kontingent: Nicht freigegeben“ mit dem Zusatz
 * „Diesen Verbrauch gibt nur der Anbieter von CLAiRIS frei. …“ (CostPanel.tsx ueberZusatz, K-5.1); dann entfällt hier der
 * gleiche erste Satz.
 */
export function kontingentWegSatz(lage: { zukaufAngeboten: boolean; link: Link | null; selbstMehrGeben: boolean; ueberSchonGenannt?: boolean }): Satz {
  const erster = 'Verbrauch über dem Kontingent gibt nur der Anbieter von CLAiRIS frei.';
  const selbst = lage.selbstMehrGeben ? ' Einer einzelnen Person mit persönlicher Grenze geben Sie selbst mehr: mit „Für diesen Monat mehr geben …“ in ihrer Zeile.' : '';
  // Spur guthaben, GU-31 (SPEC-KONTINGENT.md 14.1; welle-2/guthaben/AUFTEILUNG.md W-S11): ist Aufladen möglich, recherchiert die
  // Firma nach dem Kontingent mit zusätzlichem Guthaben weiter; „Zusatzkontingent kaufen“ ist alt.
  if (lage.zukaufAngeboten) return ohneLink(`${lage.ueberSchonGenannt ? '' : `${erster} `}Ist das Kontingent aufgebraucht, recherchiert Ihre Firma mit zusätzlichem Guthaben weiter: „Guthaben aufladen …“.${selbst}`);
  const weg = `${erster} Mehr ${MEHR_FIRMA}; `;
  return lage.link ? { vor: `${weg}schreiben Sie dazu an `, link: lage.link, nach: `.${selbst}` }
    : ohneLink(`${weg}wenden Sie sich dazu an den Anbieter von CLAiRIS.${selbst}`);
}

/** KT-24, Einzelkonto ohne angebotenen Zukauf (Runde 3, N3-09 a). */
export function anbieterEinzelkonto(link: Link | null): Satz {
  const anfang = 'Mehr Kontingent für diesen Monat gibt der Anbieter von CLAiRIS auf Anfrage frei';
  return link ? { vor: `${anfang}: `, link, nach: '.' } : ohneLink(`${anfang}.`);
}

/** KT-53, KT-54: der Schluss des Satzes zur Nachzahlung ohne angebotenen Zukauf, nach „gesperrt“ (ohne Adresse). */
export const VORSCHAU_OHNE_ZUKAUF = '; mehr Kontingent für die ganze Firma gibt auf Anfrage der Anbieter von CLAiRIS frei.';

/**
 * KT-55: an der Stelle des Kaufblocks auf „Mein Zugang“ und „Nutzung“, wenn kein Zukauf angeboten ist, für Konten mit
 * Kaufrecht. Nur mit Kontingent (Runde 3, N3-02: vor der ersten Zahlung und bei „ohne Begrenzung“ steht dort nichts). Bei Stufe
 * OK als Bedingung, ab FAST die kurze Form aus KT-35; nicht doppelt, wenn auf derselben Seite schon ein Satz mit dem Weg steht
 * (KT-41, KT-24). Beim Einzelkonto ohne „für die ganze Firma“.
 */
export function kaufErsatz(lage: { mitKontingent: boolean; stufe: Stufe | null | undefined; einzelkonto: boolean;
  wegSchonAufSeite: boolean; link: Link | null }): Satz | null {
  if (!lage.mitKontingent || lage.wegSchonAufSeite) return null;
  const ende = (anfang: string): Satz => lage.link ? { vor: `${anfang}: `, link: lage.link, nach: '.' } : ohneLink(`${anfang}.`);
  if (lage.einzelkonto) return ende('Reicht Ihr Kontingent in einem Monat nicht, gibt der Anbieter von CLAiRIS auf Anfrage mehr frei');
  if (!lage.stufe || lage.stufe === 'OK') return ende('Reicht das Kontingent in einem Monat nicht, gibt der Anbieter von CLAiRIS auf Anfrage mehr für die ganze Firma frei');
  return anbieterKurz(lage.link);
}

// --- Tage und Monate ------------------------------------------------------------------------------------------------------
const tagFormat = (zeitzone: string) => new Intl.DateTimeFormat('de-AT', { timeZone: zeitzone, day: 'numeric', month: 'long' });
const monatFormat = new Intl.DateTimeFormat('de-AT', { timeZone: 'UTC', month: 'long' });
/** „31. Oktober“; ein reiner Tag (YYYY-MM-DD) gilt als Kalendertag, ein Zeitpunkt in österreichischer Zeit. */
export function tagText(iso: string): string {
  const nurTag = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const teile = tagFormat(nurTag ? 'UTC' : 'Europe/Vienna').formatToParts(new Date(nurTag ? `${iso}T00:00:00Z` : iso));
  const teil = (typ: Intl.DateTimeFormatPartTypes) => teile.find(t => t.type === typ)?.value ?? '';
  return `${Number(teil('day'))}. ${teil('month')}`;
}
/** „Oktober“ aus dem Monat YYYY-MM. */
export function monatName(monat: string): string {
  const [jahr, nummer] = monat.split('-').map(Number);
  return monatFormat.format(new Date(Date.UTC(jahr, nummer - 1, 15)));
}

// --- Teile vom Kontingent ------------------------------------------------------------------------------------------------
/** „2 %“ bzw. „unter 1 %“ (3.4); mit persönlicher Grenze auch mit einer Nachkommastelle („16,7 %“). */
export const anteilText = (anteil: Anteil) => prozentAnzeige(anteil.prozent, { unterEinem: anteil.unterEinem });
/** Was eine Person vom Kontingent verwendet hat, mit Bezug (Regel G-19, KT-40): „3 % des Kontingents“, „unter 1 % des
 *  Kontingents“, „noch nichts verwendet“. */
export function vomKontingentText(anteil: Anteil): string {
  if (anteil.prozent === 0 && !anteil.unterEinem) return 'noch nichts verwendet';
  return `${anteilText(anteil)} des Kontingents`;
}

// --- KT-12 Zusatzkontingent -----------------------------------------------------------------------------------------------
/** Titel des zweiten Balkens mit der Größe (3.4): „Zusatzkontingent für Oktober (+ 7 %)“; ohne Kontingent der Firma (Runde 2,
 *  B = 0) ohne Größe: „Zusatzkontingent vom Anbieter von CLAiRIS für Oktober“. */
export function zusatzTitel(zusatz: Pick<ZusatzTopf, 'groesseProzent' | 'groesseUnterZehntel'>, monat: string): string {
  if (zusatz.groesseProzent === null) return `Zusatzkontingent vom Anbieter von CLAiRIS für ${monatName(monat)}`;
  return `Zusatzkontingent für ${monatName(monat)} (+ ${groesseAnzeige(zusatz.groesseProzent, zusatz.groesseUnterZehntel)})`;
}
/** Herkunft unter dem Balken: „vom Anbieter von CLAiRIS (kostenlos)“, „gekauft“ oder beides. */
export function zusatzHerkunft(herkunft: ZusatzTopf['herkunft']): string {
  const anbieter = herkunft.includes('ANBIETER'), gekauft = herkunft.includes('GEKAUFT');
  if (anbieter && gekauft) return 'vom Anbieter von CLAiRIS (kostenlos) und gekauft';
  return gekauft ? 'gekauft' : 'vom Anbieter von CLAiRIS (kostenlos)';
}
export const ZUSATZ_ERST_SATZ = 'Wird erst verwendet, wenn das Kontingent ganz verwendet ist.';
/** „Gilt bis 31. Oktober“ (2.1). */
export const giltBisText = (giltBis: string) => `Gilt bis ${tagText(giltBis)}`;
/** Rechts am Balken eines Topfs: „noch nichts verwendet“, „unter 1 % verwendet“, „35 % verwendet“. */
export function standText(stand: { prozent: number; unterEinem?: boolean }): string {
  if (stand.prozent === 0 && !stand.unterEinem) return 'noch nichts verwendet';
  return `${prozentAnzeige(stand.prozent, { unterEinem: stand.unterEinem })} verwendet`;
}

// --- KT-49 Verbrauch über dem Kontingent ---------------------------------------------------------------------------------
/** „Verbrauch über dem Kontingent: erlaubt bis 10 % · davon 4 % verwendet“; ab FAST „· Obergrenze fast erreicht“, bei
 *  ERREICHT „· Obergrenze erreicht, neue Recherchen sind bis zum Neubeginn gesperrt“ (Runde 2, RL2-03); ohne Obergrenze (nur
 *  bei „Weitere Recherchen erlauben“) „erlaubt, ohne Obergrenze“.
 *  Spur nachbesserung-m3, K-5.3a (AUFTEILUNG.md 1.3; W-S11): trägt das zusätzliche Guthaben (guthabenTraegt), sperrt der Server
 *  bei erreichter Obergrenze nicht; dann endet die Zeile mit „ · Obergrenze erreicht; Recherchen laufen jetzt über das
 *  zusätzliche Guthaben“. */
export function ueberZeile(ueber: UeberTopf, guthabenTraegt = false): string {
  const grenze = ueber.groesseProzent === null ? 'erlaubt, ohne Obergrenze' : `erlaubt bis ${prozentAnzeige(ueber.groesseProzent)}`;
  const zustand = ueber.zustand === 'ERREICHT'
    ? (guthabenTraegt ? ' · Obergrenze erreicht; Recherchen laufen jetzt über das zusätzliche Guthaben' : ' · Obergrenze erreicht, neue Recherchen sind bis zum Neubeginn gesperrt')
    : ueber.zustand === 'FAST' ? ' · Obergrenze fast erreicht' : '';
  return `Verbrauch über dem Kontingent: ${grenze} · davon ${standText(ueber)}${zustand}`;
}
/** Satz unter KT-49 und bei „Erlaubt“ in KT-31 (Frage KO-F18). */
export const UEBER_RECHNUNG_SATZ = 'CLAiRIS stellt dafür nichts von selbst in Rechnung. Ob etwas anfällt, regelt Ihre Vereinbarung mit dem Anbieter von CLAiRIS.';

// --- KT-41 Zeile über der Mitgliederliste, auf „Mein Zugang“, „Kontingent“ und „Tarif und Kosten“ -------------------------
type Topf = 'KONTINGENT' | 'ZUSATZ' | 'UEBER';
/** Der Topf, an dem die Sperre hängt (3.5): der letzte vorhandene – Verbrauch über dem Kontingent mit Obergrenze, sonst das
 *  Zusatzkontingent, sonst das Kontingent. */
export function topfDerSperre(organisation: Pick<KontingentOrganisation, 'zusatz' | 'ueber'>): Topf {
  if (organisation.ueber && organisation.ueber.zustand !== null) return 'UEBER';
  return organisation.zusatz ? 'ZUSATZ' : 'KONTINGENT';
}
export type WarnZeile = { satz: string; kaufen: boolean; weg: Satz | null };
/** Spur guthaben, KT-41 mit tragendem Guthaben (W-S11; derselbe Wortlaut wie guthabenTexte.ts GUTHABEN_TEXTE.kt41Traegt). */
export const KT41_GUTHABEN_TRAEGT = 'Das Kontingent Ihrer Firma ist aufgebraucht. Recherchen laufen jetzt über das zusätzliche Guthaben.';
/**
 * KT-41 für Konten, die das Kontingent sehen dürfen, nach der Stufe aus 3.5 (organisation.stufe) und mit dem Namen des Topfs
 * der Sperre; null bei Stufe OK oder ohne Kontingent. Als Info-Zeile ohne gelbe Fläche (Regel G-02). kaufen: mit Kaufrecht
 * und möglichem Aufladen der Textlink „Guthaben aufladen …“ dahinter (Spur guthaben, GU-31); ohne Aufladen dahinter weg, die kurze
 * Form aus KT-35.
 */
export function warnZeile(lage: { organisation: Pick<KontingentOrganisation, 'kosten' | 'zusatz' | 'ueber' | 'stufe' | 'zukaufAngeboten'> & Pick<Partial<KontingentOrganisation>, 'paket' | 'guthaben' | 'recherchen' | 'recherchenUeberGuthaben'>;
  setztZurueckAm: string; kaufrecht: boolean; link: Link | null }): WarnZeile | null {
  const { organisation } = lage;
  if (!organisation.kosten) return null;
  const topf = topfDerSperre(organisation);
  const stufe: BalkenZustand = organisation.stufe
    ?? (topf === 'UEBER' ? organisation.ueber!.zustand! : topf === 'ZUSATZ' ? organisation.zusatz!.zustand ?? 'OK' : organisation.kosten.zustand);
  if (stufe === 'OK') return null;
  // Spur guthaben, GU-31 (SPEC-KONTINGENT.md 14.1 Punkt 3; W-S11): bei 100 %, solange das zusätzliche Guthaben trägt, laufen die
  // Recherchen darüber weiter; kein Weg zum Anbieter, mit Kaufrecht dahinter „Guthaben aufladen …“.
  if (stufe === 'ERREICHT' && organisation.guthaben?.traegt === true)
    return { satz: KT41_GUTHABEN_TRAEGT, kaufen: !!organisation.zukaufAngeboten && lage.kaufrecht, weg: null };
  const tag = tagText(lage.setztZurueckAm), weich = organisation.kosten.modus === 'SOFT';
  let satz: string;
  if (topf === 'UEBER') {
    const ueber = organisation.ueber!;
    satz = stufe === 'FAST'
      ? `Der erlaubte Verbrauch über dem Kontingent ist fast erreicht: ${prozentAnzeige(ueber.prozent, { unterEinem: ueber.unterEinem }).replace(' %', '')} von ${prozentAnzeige(ueber.groesseProzent ?? 0)} verwendet. Am ${tag} beginnt das Kontingent neu.`
      : `Der erlaubte Verbrauch über dem Kontingent ist erreicht. Neue Recherchen sind bis zum ${tag} gesperrt.`;
  } else {
    const name = topf === 'ZUSATZ' ? 'Das Zusatzkontingent Ihrer Firma' : 'Das Kontingent Ihrer Firma';
    const stand = topf === 'ZUSATZ' ? organisation.zusatz! : organisation.kosten;
    satz = stufe === 'FAST'
      ? `${name} ist fast aufgebraucht: ${prozentAnzeige(stand.prozent ?? 0, { unterEinem: stand.unterEinem })} verwendet. Am ${tag} beginnt ${topf === 'ZUSATZ' ? 'das Kontingent' : 'es'} neu.`
      : `${name} ist aufgebraucht. ${weich ? 'Weitere Recherchen sind trotzdem möglich.' : `Neue Recherchen sind bis zum ${tag} gesperrt.`}`;
  }
  const zukauf = !!organisation.zukaufAngeboten;
  return { satz, kaufen: zukauf && lage.kaufrecht, weg: zukauf ? null : anbieterKurz(lage.link) };
}

// --- Feste Sätze ----------------------------------------------------------------------------------------------------------
/** KT-39: an den Feldern in Prozent und im Fenster „Mehr für …“, wenn die Firma kein Kontingent hat. */
export const KEIN_KONTINGENT_SATZ = 'Für Ihre Firma ist kein Kontingent festgelegt. Werte in Prozent lassen sich deshalb nicht setzen.';
// F-23 (PF-NB-geld-3, SPEC-geld Befund 13): ohne Kontingent ein Satz je Abschnitt statt gesperrter Felder (FRAGEN F-23-geld-2).
// F-24 (PF-TX-geld-1, SPEC-geld E-1, FRAGEN F-24-geld-1): auf „Kontingent“ steht ohne Kontingent statt der Abschnitte die eine Zeile
// regeln; die Sätze je Abschnitt bleiben für die Ausnahmen (vorhandene Stufen, Guthaben, Grenzen) und den Betreiberzweig.
export const OHNE_KONTINGENT = {
  stand: 'Für Ihre Firma ist keine Obergrenze festgelegt: Es gibt keine Sperre.',
  regeln: 'Zusätzliches Guthaben, Vorgaben der Firma, Warnungen, Kontingentstufen und persönliche Grenzen gelten erst, wenn der Anbieter von CLAiRIS ein Kontingent festlegt.',
  vorgaben: 'Vorgaben zum Aufbrauchen des Kontingents gelten erst, wenn der Anbieter von CLAiRIS ein Kontingent festlegt.',
  warnungen: 'Warnungen in Prozent gibt es erst, wenn Ihre Firma ein Kontingent hat.',
  guthaben: 'Zusätzliches Guthaben wird erst verwendet, wenn Ihre Firma ein Kontingent hat und es aufgebraucht ist.',
  stufen: 'Kontingentstufen gelten in Prozent des Kontingents. Ohne Kontingent lassen sie sich nicht anlegen.',
  grenzen: 'Persönliche Grenzen gelten in Prozent des Kontingents. Ohne Kontingent lassen sie sich nicht festlegen.',
  auswahlZuerst: 'Wählen Sie zuerst in der Tabelle Personen aus.',
  betreiberOhne: 'Diese Firma hat kein Kontingent; erhöhen lässt sich nichts.',
  ohneBegrenzung: 'Ohne Begrenzung',
  recherchen: 'Recherchen diesen Monat',
} as const;
/** KT-45: unter jeder Tabelle mit Werten je Person. */
export const ABGERUNDET_SATZ = 'Die Werte je Person sind abgerundet. Zusammen weichen sie deshalb etwas von der Zeile „Ganzes Team“ ab.';
/** KT-46: wo ein früherer Monat gezeigt wird. */
export const FRUEHERER_MONAT_SATZ = 'Frühere Monate sind am heutigen Kontingent Ihrer Firma gemessen.';
/** KT-62 (der erste Satz von KT-30): was ein Kontingent ist. */
export const KONTINGENT_IST_SATZ = 'Kontingent ist die Menge an KI-Nutzung, die im Preis Ihres Tarifs enthalten ist.';
